/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { IonButton, IonIcon, IonModal } from "@ionic/react";
import {
  DataSharingFollower,
  Profile,
} from "./datasharing/DataSharingTypes";
import {
  alertCircleOutline,
  callOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  closeOutline,
  eyeOutline,
  informationCircleOutline,
  mailOutline,
  peopleOutline,
  timeOutline,
} from "ionicons/icons";
import {
  acceptFollowRequest,
  rejectFollowRequest,
} from "../../services/datashare";
import { requestTitle } from "./datasharing/requestTitle";
import { useStorage } from "../../contexts/StorageContext";
import { getIonicStorage, ionicStorage } from "../../services/storageService";
// Aliased: `PendingRequestListItem` below has its own `getInitials` closure (which keeps a
// `username` step this one does not), so the shared helper must not shadow it.
import {
  getInitials as getProfileInitials,
  getUserName,
} from "../../utils/helpers";

// Extended interface for internal use with display properties
export interface DisplayPendingRequest extends DataSharingFollower {
  name: string;
  email: string;
  phone: string | null;
  dateAdded: string;
  initials: string;
  avatar?: string | null;
}

interface PendingRequestsProps {
  actionRequired?: DisplayPendingRequest[]; // made optional so component can mount without props
  pageType?: string; // New prop to determine the page type
}
interface PendingRequestListItemProps {
  request: DisplayPendingRequest;
  onAccept: (request: DisplayPendingRequest) => void;
  onReject: (request: DisplayPendingRequest) => void;
  isLoading?: boolean;
}
export const PendingRequestListItem: React.FC<PendingRequestListItemProps> = ({
  request,
  onAccept,
  onReject,
  isLoading = false,
}) => {
  const [errorImage, setErrorImage] = useState(false);
  // ⛔ Both helpers destructured `profile` off the request and then dereferenced it. A request
  // with no expanded profile threw here, and because App.tsx's ErrorBoundary renders `null`
  // (TD-165) one such row blanks the WHOLE app. Guard the OBJECT, not only its fields.
  const getDisplayName = (): string => {
    const profile = request?.profile;
    if (!profile) return "Unknown User";
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return (
      profile.first_name ||
      profile.last_name ||
      profile.username ||
      profile.email ||
      profile.phone ||
      "Unknown User"
    );
  };

  const getInitials = (): string => {
    const profile = request?.profile;
    if (!profile) return "U";
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name.charAt(0)}${profile.last_name.charAt(
        0
      )}`.toUpperCase();
    }
    if (profile.first_name) {
      return profile.first_name.charAt(0).toUpperCase();
    }
    if (profile.last_name) {
      return profile.last_name.charAt(0).toUpperCase();
    }
    if (profile.username) {
      return profile.username.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const getAvatarColor = (): string => {
    switch (request.type) {
      case "incoming_follower_request":
        return "bg-cyan-600";
      case "incoming_following_request":
        return "bg-green-600";
      default:
        return "bg-amber-600";
    }
  };

  const getRequestTypeDisplay = () => {
    if (request.type === "incoming_follower_request") {
      return (
        <div className="flex items-center text-cyan-600 text-sm">
          <IonIcon
            icon={eyeOutline}
            className="mr-1"
            style={{ fontSize: "14px" }}
          />
          <span>Wants to follow you</span>
        </div>
      );
    }

    if (request.type === "incoming_following_request") {
      return (
        <div className="flex items-center text-green-600 text-sm">
          <IonIcon
            icon={peopleOutline}
            className="mr-1"
            style={{ fontSize: "14px" }}
          />
          <span>Wants you to follow them</span>
        </div>
      );
    }

    return (
      <div className="flex items-center text-amber-600 text-sm">
        <IonIcon
          icon={alertCircleOutline}
          className="mr-1"
          style={{ fontSize: "14px" }}
        />
        <span>Pending request</span>
      </div>
    );
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year:
          date.getFullYear() !== new Date().getFullYear()
            ? "numeric"
            : undefined,
      });
    } catch {
      return "Recently";
    }
  };

  return (
    <div
      className={`flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${
        isLoading ? "opacity-75" : ""
      }`}
    >
      {/* Left side - Avatar and user info */}
      <div className="flex items-center w-[60%] min-w-0">
        {/* Avatar */}
        <div
          className={`w-10 h-10 rounded-full ${getAvatarColor()} text-white flex items-center justify-center font-bold shadow-sm flex-shrink-0`}
        >
          {!errorImage && request.profile?.avatar ? (
            <img
              src={request.profile?.avatar}
              alt={getDisplayName()}
              className="w-full h-full rounded-full object-cover"
              onError={(e) => {
                // Hide image and fallback to initials if avatar fails to load
                setErrorImage(true);
                // Optionally, you could trigger a re-render or set a state to show initials
              }}
            />
          ) : (
            getInitials()
          )}
        </div>

        {/* User information */}
        <div className="ml-3 flex-1 min-w-0">
          {/* Name */}
          <div className="flex items-center">
            <span
              className="font-medium truncate block"
              style={{
                maxWidth: "150px",
              }}
              title={getDisplayName()}
            >
              {getDisplayName()}
            </span>
          </div>

          {/* Request type */}
          <div className="mt-1">{getRequestTypeDisplay()}</div>

          {/* Contact information */}
          <div className="mt-1 space-y-1">
            {request.profile?.email && (
              <div className="flex items-center text-xs text-gray-500">
                <IonIcon
                  icon={mailOutline}
                  className="mr-1"
                  style={{ fontSize: "12px" }}
                />
                <span
                  className="truncate"
                  style={{ maxWidth: "120px" }}
                  title={request.profile?.email}
                >
                  {request.profile?.email}
                </span>
              </div>
            )}

            {request.profile?.phone && (
              <div className="flex items-center text-xs text-gray-500">
                <IonIcon
                  icon={callOutline}
                  className="mr-1"
                  style={{ fontSize: "12px" }}
                />
                <span
                  className="truncate"
                  style={{ maxWidth: "120px" }}
                  title={request.profile?.phone}
                >
                  {request.profile?.phone}
                </span>
              </div>
            )}
          </div>

          {/* Date information */}
          {request.created_at && (
            <div className="flex items-center text-xs text-gray-400 mt-1">
              <IonIcon
                icon={timeOutline}
                className="mr-1"
                style={{ fontSize: "12px" }}
              />
              <span>Requested {formatDate(request.created_at)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Action buttons */}
      <div className="flex justify-end items-center w-[40%] flex-shrink-0 space-x-2">
        <button
          className={`flex items-center justify-center px-3 py-1 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={() => onAccept(request)}
          disabled={isLoading}
          aria-label={`Accept request from ${getDisplayName()}`}
        >
          <IonIcon
            icon={checkmarkCircleOutline}
            className="mr-1"
            style={{ fontSize: "14px" }}
          />
          Accept
        </button>
        <button
          onClick={() => onReject(request)}
          disabled={isLoading}
          className={`flex items-center justify-center px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
          aria-label={`Reject request from ${getDisplayName()}`}
        >
          <IonIcon icon={closeCircleOutline} style={{ fontSize: "14px" }} />
        </button>
      </div>
    </div>
  );
};

const EmptyPendingState: React.FC = () => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
      <IonIcon
        icon={alertCircleOutline}
        className="mx-auto text-gray-400 mb-4"
        style={{ fontSize: "64px" }}
      />
      <h3 className="text-lg font-semibold text-gray-700 mb-2">
        No Pending Requests
      </h3>
      <p className="text-gray-600">
        You don&apos;t have any pending requests at the moment. When someone
        want&apos;s to follow you or asks you to follow them, they&apos;ll
        appear here.
      </p>
    </div>
  );
};
// Fix: use a normal functional component signature (not a type used as JSX)
const PendingRequestsPage: React.FC<PendingRequestsProps> = ({
  actionRequired = [], // default to an empty array when prop is not provided
  pageType = "Following", // default to "Following" if not provided
}: PendingRequestsProps) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [pendingOpen, setPendingOpen] = useState(true);
  const { storage, isStorageReady } = useStorage();
  const [actionsRequiredItems, setActionsRequiredItems] = useState<
    DisplayPendingRequest[]
  >(actionRequired || []);
  const [loadingRequestId, setLoadingRequestId] = useState<number | null>(null);

  // const onCancelRequest = (request: DisplayPendingRequest) => {
  //   // Implement reject logic here
  //   console.log("Rejecting request from:", request);
  // };
  useEffect(() => {
    setActionsRequiredItems(actionRequired || []);
  }, [actionRequired]);

  const confirmAcceptRequest = async (selectedRequest?: any): Promise<void> => {
    if (!selectedRequest) return;
    setActionsRequiredItems((prev) =>
      prev.filter((req) => req.id !== selectedRequest.id)
    );
    if (selectedRequest.type === "incoming_follower_request") {
      await acceptFollowRequest({ relationship_id: selectedRequest.id });
    } else if (selectedRequest.type === "incoming_following_request") {
      await acceptFollowRequest({ relationship_id: selectedRequest.id });
    }
  };
  const confirmRejectRequest = async (selectedRequest?: any): Promise<void> => {
    if (!selectedRequest) return;
    try {
      setActionsRequiredItems((prev) =>
        prev.filter((req) => req.id !== selectedRequest.id)
      );
      await rejectFollowRequest({ relationship_id: selectedRequest.id });
    } catch (error) {
      console.error("Failed to reject request:", error);
    }
  };

  // Add filteredRequests based on pageType:
  // Use forEach to categorize requests, then use filter for final selection
  const followerRequests: DisplayPendingRequest[] = [];
  const followingRequests: DisplayPendingRequest[] = [];
  actionsRequiredItems.forEach((req) => {
    if (req.type === "incoming_follower_request") followerRequests.push(req);
    if (req.type === "incoming_following_request") followingRequests.push(req);
  });

  let filteredRequests: DisplayPendingRequest[] = [];
  const normalizedPageType = (pageType || "").toLowerCase();
  if (normalizedPageType === "followers") {
    filteredRequests = followerRequests.filter(Boolean);
  } else if (normalizedPageType === "following") {
    filteredRequests = followingRequests.filter(Boolean);
  } else {
    // default: include both incoming follower and following requests
    filteredRequests = actionsRequiredItems.filter(
      (req) =>
        req.type === "incoming_follower_request" ||
        req.type === "incoming_following_request"
    );
  }

  // Keep only states that are used in this file
  return (
    <>
      {popupOpen && (
        <IonModal
          isOpen={popupOpen}
          onDidDismiss={() => setPopupOpen(false)}
          presentingElement={document.querySelector("ion-app") || undefined}
          canDismiss={true}
          showBackdrop={true}
          backdropDismiss={true}
          style={{
            "--background": "transparent",
            "--box-shadow": "none",
            "--border-radius": "20px",
          }}
        >
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "white",
              borderRadius: "20px",
              maxHeight: "70vh",
              overflow: "hidden",
              margin: "20px",
            }}
          >
            {/* Header with close button and title */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "3px",
              }}
            >
              <div style={{ width: "40px" }}></div>
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: "0",
                  color: "#000",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                {selectedProfile?.first_name && selectedProfile?.last_name
                  ? `${selectedProfile.first_name} ${selectedProfile.last_name}`
                  : selectedProfile?.first_name || ""}{" "}
              </h2>
              <br />

              <button
                onClick={() => setPopupOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#999",
                  cursor: "pointer",
                  padding: "8px",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "top",
                  justifyContent: "top",
                }}
              >
                <IonIcon icon={closeOutline} style={{ fontSize: "24px" }} />
              </button>
            </div>
            <p className=" flex text-xl text-center items-center justify-center">
              {requestTitle(selectedProfile)}
            </p>
            <small
              className="text-gray-500"
              style={{
                textAlign: "center",
                justifyContent: "center",
                display: "flex",
              }}
            >
              {(() => {
                const now = new Date();
                // Parse created_at as local time if no timezone, otherwise as UTC
                const created = new Date(selectedProfile?.created_at);
                const diff = Math.floor(
                  (now.getTime() - created.getTime()) / 1000
                );
                if (diff < 60) return "just now";
                if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
                if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
                return `${Math.floor(diff / 86400)} days ago`;
              })()}
            </small>
            <div className=" flex text-xl text-center  mb-6  items-center justify-center">
              {selectedProfile?.email || ""}
              <br />
              {selectedProfile?.phone || ""}
            </div>
            {/* Message and simple action (replaces dynamic/undefined button rendering) */}
            <div style={{ padding: "0 16px 16px" }}>
              <div
                style={{
                  padding: "0 ",
                  display: "flex",
                  gap: 12,
                  flexDirection: "column",
                }}
              >
                <IonButton
                  expand="block"
                  fill={"solid"}
                  onClick={() => {
                    const action = async (selectedProfile: any) => {
                      setPopupOpen(false);
                      await confirmAcceptRequest(selectedProfile);
                    };
                    action(selectedProfile);
                  }}
                  className={"primary-button w-full"}
                  style={{
                    "--border-radius": "30px",
                    fontSize: "18px",
                    fontWeight: "500",
                  }}
                >
                  {" "}
                  Accept{" "}
                </IonButton>

                <IonButton
                  expand="block"
                  fill={"outline"}
                  onClick={() => {
                    const action = async (selectedProfile: any) => {
                      setPopupOpen(false);
                      await confirmRejectRequest(selectedProfile);
                    };
                    action(selectedProfile);
                  }}
                  className={"tertiary-button w-full"}
                  style={{
                    "--border-radius": "30px",
                    fontSize: "18px",
                    fontWeight: "500",
                    "--color": "#ffffff !important",
                    borderRadius: "30px",
                  }}
                >
                  Reject
                </IonButton>
              </div>
            </div>
          </div>
        </IonModal>
      )}{" "}
      {filteredRequests.length > 0 && (
        <div>
          <div className="flex items-center ">
            <p className="sub-section-title">Pending Requests</p>
          </div>
          <div
            className=" "
            style={{
              overflowY: "auto",
              maxHeight: "100%",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {filteredRequests.length > 0 && (
              <div className="rounded-xl bg-white shadow-sm overflow-hidden sm:mx-0 border">
                {pendingOpen && (
                  <div
                    id="pending-requests-list"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {filteredRequests.map((request) => (
                      <div key={`incoming-${request.id}`} className="p-1">
                        <div className="flex items-center justify-between p-2">
                          <div className="flex items-center">
                            <div
                              className="w-10 h-10 rounded-full bg-[#007C9E]    text-white flex items-center justify-center font-bold"
                              onClick={() => {
                                setSelectedProfile(request);
                                setPopupOpen(true);
                              }}
                            >
                              {getProfileInitials(
                                request.profile?.first_name,
                                request.profile?.last_name
                              )}
                            </div>
                            <span
                              className="ml-3 font-medium truncate"
                              style={{ maxWidth: "120px" }}
                              onClick={() => {
                                console.log("Selected profile:", request);
                                // ensure modal gets the full request (so type is available)
                                setSelectedProfile(request);

                                setPopupOpen(true);
                              }}
                            >
                              {getUserName(request.profile)}
                            </span>
                          </div>
                          <div className="flex items-center cursor-pointer text-[#0778CF]">
                            <p
                              className="px-1 py-1"
                              style={{
                                fontFamily: "Figtree, sans-serif",
                                fontWeight: 500,
                                fontStyle: "normal",
                                fontSize: "13px",
                                lineHeight: "22px",
                              }}
                              onClick={() => confirmAcceptRequest(request)}
                            >
                              Accept
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Bottom spacing */}
            <div className="pb-4" />
          </div>
        </div>
      )}
    </>
  );
};

export default PendingRequestsPage;
